/*======================================================================================================================
  * Bluebell: src/app/state/annotations/annotations.effects.ts
  * Last Edited: 15/07/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EffectHandlerService } from '@services/effect-handler.service';
import { deselectResults, selectAllResults, selectResults, setSelectedResults } from '@state/results/results.actions';
import { selectSelectedResultsIds } from '@state/results/results.selectors';
import {
  clearCasesWithAnnotations,
  deleteAnnotation,
  deleteAnnotationFailure,
  deleteAnnotationSuccess,
  loadAnnotations,
  loadAnnotationsFailure,
  loadAnnotationsSuccess,
  listCasesWithAnnotations,
  listCasesWithAnnotationsFailure,
  listCasesWithAnnotationsSuccess,
  reselectLoadedAnnotations,
  setAnnotationApproval,
  setAnnotationApprovalFailure,
  setAnnotationApprovalSuccess,
  updateAnnotationDescription,
  updateAnnotationDescriptionFailure,
  updateAnnotationDescriptionSuccess,
  updateAnnotationOrder,
  updateAnnotationOrderFailure,
  updateAnnotationOrderSuccess,
} from '@state/annotations/annotations.actions';
import { Annotation, CaseWithAnnotation, DeleteAnnotationResponse } from '@state/annotations/annotations.interfaces';
import { selectLoadedAnnotationCases } from '@state/annotations/annotations.selectors';
import { ApiService } from '@state/api.service';
import { selectPostproVariable } from '@state/postpro/postpro.actions';
import { filter, map, switchMap } from 'rxjs/operators';


@Injectable()
export class AnnotationsEffects {
  private actions$ = inject(Actions);
  private api = inject(ApiService);
  private store = inject(Store);
  private handler = inject(EffectHandlerService);


  loadCasesWithAnnotations$ = createEffect(() => this.actions$.pipe(
    ofType(listCasesWithAnnotations),
    switchMap(({ caseIds }) => this.api.listCasesWithAnnotations(caseIds).pipe(
      this.handler.effectResponse({
        success: (casesWithAnnotations: CaseWithAnnotation[]) => listCasesWithAnnotationsSuccess({ casesWithAnnotations }),
        retry: listCasesWithAnnotations({ caseIds }),
        failure: listCasesWithAnnotationsFailure(),
        failureMessage: 'Could not load cases with annotations'
      })
    ))
  ));

  loadAnnotationsForCase$ = createEffect(() => this.actions$.pipe(
    ofType(loadAnnotations),
    switchMap(({ uuid }) => this.api.listAnnotations([uuid]).pipe(
      this.handler.effectResponse({
        success: (annotations: Annotation[]) => loadAnnotationsSuccess({ annotations, uuid }),
        retry: loadAnnotations({ uuid }),
        failure: loadAnnotationsFailure(),
        failureMessage: 'Could not load annotations'
      })
    ))
  ));

  deleteAnnotation$ = createEffect(() => this.actions$.pipe(
    ofType(deleteAnnotation),
    switchMap(({ uuid }) => this.api.deleteAnnotation(uuid).pipe(
      this.handler.effectResponse({
        success: (response: DeleteAnnotationResponse) => deleteAnnotationSuccess({ uuid, response }),
        retry: deleteAnnotation({ uuid }),
        failure: deleteAnnotationFailure(),
        failureMessage: 'Could not delete annotation'
      })
    ))
  ));

  updateDescription$ = createEffect(() => this.actions$.pipe(
    ofType(updateAnnotationDescription),
    switchMap(({ uuid, description }) => this.api.updateAnnotationDescription(uuid, description).pipe(
      this.handler.effectResponse({
        success: (annotation: Annotation) => updateAnnotationDescriptionSuccess({ annotation }),
        retry: updateAnnotationDescription({ uuid, description }),
        failure: updateAnnotationDescriptionFailure(),
        failureMessage: 'Could not update annotation description'
      })
    ))
  ));

  updateOrder$ = createEffect(() => this.actions$.pipe(
    ofType(updateAnnotationOrder),
    switchMap(({ caseId, orderData }) => this.api.updateAnnotationOrder(caseId, orderData).pipe(
      this.handler.effectResponse({
        success: (annotations: Annotation[]) => updateAnnotationOrderSuccess({ annotations }),
        retry: updateAnnotationOrder({ caseId, orderData }),
        failure: updateAnnotationOrderFailure(),
        failureMessage: 'Could not update annotation order'
      })
    ))
  ));

  setAnnotationApproval$ = createEffect(() => this.actions$.pipe(
    ofType(setAnnotationApproval),
    switchMap(({ uuid, approve }) => this.api.setAnnotationApproval(uuid, approve).pipe(
      this.handler.effectResponse({
        success: (annotation: Annotation) => setAnnotationApprovalSuccess({ annotation }),
        retry: setAnnotationApproval({ uuid, approve }),
        failure: setAnnotationApprovalFailure(),
        failureMessage: 'Could not set annotation approval'
      })
    ))
  ));

  onResultSelection$ = createEffect(() => this.actions$.pipe(
    ofType(
      selectResults,
      selectAllResults,
      setSelectedResults,
      deselectResults,
    ),
    concatLatestFrom(() => this.store.select(selectSelectedResultsIds)),
    map(([, caseIds]: [unknown, string[]]) => caseIds?.length
      ? listCasesWithAnnotations({ caseIds })
      : clearCasesWithAnnotations()
    ),
  ));

  /**
   * Component Actions
   */

  onCaseSelect$ = createEffect(() => this.actions$.pipe(
    ofType(selectPostproVariable),
    filter(({ postproType }) => postproType === 'annotation'),
    concatLatestFrom(() => this.store.select(selectLoadedAnnotationCases)),
    map(([{ variable }, loadedCases]) => {
      if (loadedCases.includes(variable)) {
        return reselectLoadedAnnotations({ uuid: variable });
      } else {
        return loadAnnotations({ uuid: variable });
      }
    }),
  ));
}
